// 'use client'
import type { AfterResponseHook, BeforeRequestHook } from '@cdc-internal/fe-toolbox/libs/fetcher'
import { FetchError, FetchErrorReason } from '@cdc-internal/fe-toolbox/libs/fetcher'

import { LOCAL_STORAGE_NAMESPACE } from '@/configs'
import type {
  BackendApiResponseBody,
  FetcherCustomData,
  StandardApiResponseBody,
} from '@/types/api'

/* -------------------------------------------------------------------------- */
/*                            Before request hooks                            */
/* -------------------------------------------------------------------------- */

export const appendCustomHeaders: BeforeRequestHook = (request, options) => {
  if ((options.customData as FetcherCustomData)?.appendTMXSessionIdHeader) {
    const tmxSessionId = window.sessionStorage.getItem(LOCAL_STORAGE_NAMESPACE + 'tmxSessionId')
    if (tmxSessionId) {
      request.headers.set('Tmx-Session-Id', tmxSessionId)
    }
  }
}

/* -------------------------------------------------------------------------- */
/*                            After response hooks                            */
/* -------------------------------------------------------------------------- */

// Check the business error response - 200 status code with {ok: false} - and throw FetchError.
// Clients will consider it as a failed request and handle it accordingly in catch block.
export const checkBusinessErrorWith200Response: AfterResponseHook = async (
  response,
  _,
  options,
) => {
  if ((options.customData as FetcherCustomData)?.ignoreBusinessErrorWith200Response) return

  if (response.ok && response.headers.get('content-type')?.includes('application/json')) {
    // responseJson may be null if the response body is empty
    const responseJson = (await response
      .clone()
      .json()
      .catch(() => null)) as StandardApiResponseBody<BackendApiResponseBody>

    if (responseJson?.data?.ok === false) {
      throw new FetchError(FetchErrorReason.ResponseError, {
        statusCode: response.status,
        response,
        responseParsedBody: responseJson,
      })
    }
  }
}

// Handle 404 errors when silent404 is enabled
export const handleSilent404 = (async (response: Response, _: Request, options: any) => {
  if ((options.customData as FetcherCustomData)?.silent404 && response.status === 404) {
    return new Response(null, { status: 200, statusText: 'OK' })
  }
}) as AfterResponseHook
